import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  Chip,
} from '@mui/material';
import {
  DownloadRounded,
  TrendingDownRounded,
  TrendingUpRounded,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

const cardSx = {
  borderRadius: 2.5,
  border: '1px solid #e2e8f0',
  bgcolor: '#fff',
  boxShadow: 'none',
  overflow: 'hidden',
};

const thSx = {
  fontSize: '0.7rem',
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: '1px solid #e2e8f0',
  bgcolor: '#fafbfc',
};

const tdSx = {
  fontSize: '0.8rem',
  color: '#334155',
  borderBottom: '1px solid #f1f5f9',
};

const t25ChipSx = {
  height: 22,
  fontSize: '0.66rem',
  fontWeight: 700,
  borderRadius: '999px',
};

function SummaryStat({ label, value, sub, tone = '#1e293b', featured = false, badge = null, icon = null }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        py: featured ? 0.45 : 0.15,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </Typography>
        {badge ? (
          <Box
            sx={{
              px: 0.8,
              py: 0.35,
              borderRadius: '999px',
              bgcolor: alpha(tone, 0.12),
              color: tone,
              fontSize: '0.66rem',
              fontWeight: 800,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {badge}
          </Box>
        ) : null}
      </Box>
      <Box sx={{ mt: 0.45, display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        {icon ? (
          <Box
            sx={{
              width: featured ? 42 : 24,
              height: featured ? 42 : 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(tone, featured ? 0.12 : 0.08),
              color: tone,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        ) : null}
        <Typography sx={{ fontSize: featured ? { xs: '2rem', md: '2.35rem' } : '1.15rem', fontWeight: 900, color: tone, lineHeight: 1.02, fontFamily: 'monospace', minWidth: 0, letterSpacing: featured ? '-0.03em' : 0 }}>
          {value}
        </Typography>
      </Box>
      {sub ? (
        <Typography sx={{ mt: 0.55, fontSize: featured ? '0.82rem' : '0.68rem', color: featured ? alpha(tone, 0.82) : '#94a3b8', lineHeight: 1.45 }}>
          {sub}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function ModelPortfolioBoard({ boardData }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'vi' ? 'vi-VN' : 'en-US';
  const allocationExportRef = useRef(null);
  const actionLogExportRef = useRef(null);
  const [exportingAllocationImage, setExportingAllocationImage] = useState(false);
  const [exportingActionLogImage, setExportingActionLogImage] = useState(false);

  if (!boardData) {
    return null;
  }

  const overview = boardData.overview || {};
  const allocationPriority = (item) => {
    if (item.t25_status === 'ready') {
      return 0;
    }

    if (item.t25_status === 'partial') {
      return 1;
    }

    if (typeof item.holding_days === 'number') {
      if (item.holding_days === 1) {
        return 2;
      }

      if (item.holding_days <= 0) {
        return 3;
      }
    }

    return 2;
  };

  const allocations = [...(boardData.allocations || [])].sort((left, right) => {
    const priorityDiff = allocationPriority(left) - allocationPriority(right);
    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    return String(left.ticker || '').localeCompare(String(right.ticker || ''));
  });
  const actionLog = boardData.action_log || [];
  const timeline = (boardData.timeline || []).map((point) => ({
    ...point,
    shortDate: new Date(point.trade_date).toLocaleDateString(locale, { month: 'short', day: 'numeric' }),
  }));

  const formatCurrency = (value) => new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(value || 0);

  const formatPct = (value, digits = 2) => `${value >= 0 ? '+' : ''}${Number(value || 0).toFixed(digits)}%`;
  const navTone = overview.daily_nav_change >= 0 ? '#10b981' : '#ef4444';
  const currentNav = Number(overview.current_nav || 0);
  const navCash = Number(overview.nav_cash || 0);
  const navStocks = Number(overview.nav_stocks || 0);
  const cashRatio = currentNav > 0 ? Math.max(0, Math.min(100, (navCash / currentNav) * 100)) : 0;
  const stockRatio = currentNav > 0 ? Math.max(0, Math.min(100, (navStocks / currentNav) * 100)) : 0;
  const formatHoldingPeriod = (item) => {
    let holdingDays = item.holding_days;

    if ((holdingDays == null || Number.isNaN(Number(holdingDays))) && item.first_buy_date) {
      const startDate = new Date(item.first_buy_date);
      const endDate = new Date(boardData.as_of_date || new Date().toISOString().slice(0, 10));

      if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
        const millisPerDay = 24 * 60 * 60 * 1000;
        holdingDays = Math.max(0, Math.floor((endDate - startDate) / millisPerDay));
      }
    }

    if (holdingDays == null || Number.isNaN(Number(holdingDays))) {
      return '—';
    }

    if (Number(holdingDays) <= 0) {
      return t('paperTrading.modelBoard.holdingPeriodToday');
    }

    if (Number(holdingDays) === 1) {
      return t('paperTrading.modelBoard.holdingPeriodOneDay');
    }

    return t('paperTrading.modelBoard.holdingPeriodDays', { count: Number(holdingDays) });
  };

  const handleExportAllocationImage = async () => {
    if (!allocationExportRef.current || exportingAllocationImage) {
      return;
    }

    try {
      setExportingAllocationImage(true);
      const canvas = await html2canvas(allocationExportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });

      const link = document.createElement('a');
      const asOfDate = boardData.as_of_date || new Date().toISOString().slice(0, 10);
      link.download = `portfolio-allocation-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setExportingAllocationImage(false);
    }
  };

  const handleExportActionLogImage = async () => {
    if (!actionLogExportRef.current || exportingActionLogImage) {
      return;
    }

    try {
      setExportingActionLogImage(true);
      const canvas = await html2canvas(actionLogExportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });

      const link = document.createElement('a');
      const asOfDate = boardData.as_of_date || new Date().toISOString().slice(0, 10);
      link.download = `portfolio-action-log-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setExportingActionLogImage(false);
    }
  };

  const getT25Meta = (item) => {
    if (item.t25_status === 'ready') {
      return {
        label: t('paperTrading.modelBoard.t25Statuses.ready'),
        sx: {
          ...t25ChipSx,
          bgcolor: alpha('#10b981', 0.10),
          color: '#059669',
        },
      };
    }

    if (item.t25_status === 'partial') {
      return {
        label: 'T2.5',
        sx: {
          ...t25ChipSx,
          bgcolor: alpha('#f59e0b', 0.12),
          color: '#d97706',
        },
      };
    }

    if (typeof item.holding_days === 'number') {
      if (item.holding_days <= 0) {
        return {
          label: 'T0',
          sx: {
            ...t25ChipSx,
            bgcolor: alpha('#64748b', 0.10),
            color: '#475569',
          },
        };
      }

      if (item.holding_days === 1) {
        return {
          label: 'T1',
          sx: {
            ...t25ChipSx,
            bgcolor: alpha('#64748b', 0.10),
            color: '#475569',
          },
        };
      }
    }

    return {
      label: 'T2.5',
      sx: {
        ...t25ChipSx,
        bgcolor: alpha('#64748b', 0.10),
        color: '#475569',
      },
    };
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const item = payload[0].payload;
    return (
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.25, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', mb: 0.4 }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: item.daily_pnl_pct >= 0 ? '#10b981' : '#ef4444' }}>
          {t('paperTrading.modelBoard.dailyChangePct')}: <strong>{formatPct(item.daily_pnl_pct)}</strong>
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: item.total_pnl_pct >= 0 ? '#10b981' : '#ef4444' }}>
          {t('paperTrading.modelBoard.portfolioGrowthPct')}: <strong>{formatPct(item.total_pnl_pct)}</strong>
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          ...cardSx,
          p: 0,
          position: 'relative',
          background: 'linear-gradient(180deg, #ffffff 0%, #fdfefe 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.08), transparent 34%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.08), transparent 30%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ position: 'relative', px: { xs: 2, sm: 3 }, pt: { xs: 2.2, sm: 2.6 }, pb: 1.7, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem' }, fontWeight: 850, color: '#1e293b', letterSpacing: '-0.03em', lineHeight: 1.08 }}>
              {t('paperTrading.modelBoard.snapshot')}
            </Typography>
            <Typography sx={{ mt: 0.42, fontSize: '0.8rem', color: '#94a3b8', maxWidth: 560, lineHeight: 1.45 }}>
              {t('paperTrading.modelBoard.snapshotSubtitle')}
            </Typography>
          </Box>
          {boardData.as_of_date ? (
            <Chip
              label={`${t('paperTrading.modelBoard.asOf')} ${new Date(boardData.as_of_date).toLocaleDateString(locale)}`}
              size="small"
              sx={{ height: 44, px: 0.8, fontSize: '0.92rem', fontWeight: 800, bgcolor: 'rgba(248, 250, 252, 0.92)', color: '#475569', borderRadius: '999px', '& .MuiChip-label': { px: 1.2 } }}
            />
          ) : null}
        </Box>

        <Box sx={{ position: 'relative', px: { xs: 2, sm: 3 }, pb: { xs: 2.2, sm: 2.8 }, display: 'flex', flexDirection: 'column', gap: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.3fr) minmax(280px, 0.7fr)' }, gap: { xs: 2.2, md: 3 }, alignItems: 'end' }}>
            <Box sx={{ minWidth: 0 }}>
              <SummaryStat
                label={t('paperTrading.modelBoard.currentNav')}
                value={formatCurrency(overview.current_nav)}
                sub={t('paperTrading.modelBoard.navComposition', {
                  cash: formatCurrency(overview.nav_cash),
                  stocks: formatCurrency(overview.nav_stocks),
                })}
                featured
                icon={<TrendingUpRounded sx={{ fontSize: 24 }} />}
                tone={'#24324a'}
              />
              <Box sx={{ mt: 1.35, maxWidth: 420 }}>
                <Box sx={{ height: 10, borderRadius: '999px', bgcolor: '#e2e8f0', overflow: 'hidden', display: 'flex' }}>
                  <Box sx={{ width: `${stockRatio}%`, bgcolor: '#2563eb', transition: 'width 200ms ease' }} />
                  <Box sx={{ width: `${cashRatio}%`, bgcolor: '#f59e0b', transition: 'width 200ms ease' }} />
                </Box>
                <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#2563eb', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b' }}>
                      {`${stockRatio.toFixed(1)}%`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#f59e0b', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b' }}>
                      {`${cashRatio.toFixed(1)}%`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                minWidth: 0,
                alignSelf: 'stretch',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'stretch',
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  width: '100%',
                  pl: { xs: 0, md: 2.5 },
                }}
              >
                <SummaryStat
                  label={t('paperTrading.modelBoard.dailyNavChange')}
                  value={formatCurrency(overview.daily_nav_change)}
                  sub={t('paperTrading.modelBoard.vsPreviousSession', {
                    pct: formatPct(overview.daily_nav_change_pct),
                  })}
                  featured
                  icon={overview.daily_nav_change >= 0 ? <TrendingUpRounded sx={{ fontSize: 24 }} /> : <TrendingDownRounded sx={{ fontSize: 24 }} />}
                  tone={navTone}
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 2,
              pt: 1.8,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: { xs: 1.15, sm: 1.8 },
              bgcolor: alpha('#f8fafc', 0.72),
              borderRadius: 2.25,
              px: { xs: 1.2, sm: 1.5 },
              py: { xs: 1.25, sm: 1.4 },
            }}
          >
            <Box sx={{ minWidth: 0, position: 'relative', pr: { sm: 1 } }}>
              <Box sx={{ width: 44, height: 4, borderRadius: '999px', bgcolor: overview.total_pnl_pct >= 0 ? alpha('#10b981', 0.9) : alpha('#ef4444', 0.9), mb: 1.05 }} />
              <SummaryStat
                label={t('paperTrading.modelBoard.totalPnl')}
                value={formatPct(overview.total_pnl_pct)}
                sub={formatCurrency(overview.total_pnl)}
                tone={overview.total_pnl_pct >= 0 ? '#10b981' : '#ef4444'}
              />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                pl: { sm: 1 },
                pt: { xs: 0.15, sm: 0 },
              }}
            >
              <Box sx={{ width: 44, height: 4, borderRadius: '999px', bgcolor: overview.realized_pnl_pct >= 0 ? alpha('#2563eb', 0.9) : alpha('#ef4444', 0.9), mb: 1.05 }} />
              <SummaryStat
                label={t('paperTrading.modelBoard.realizedPnl')}
                value={formatPct(overview.realized_pnl_pct)}
                sub={t('paperTrading.modelBoard.realizedPnlSubtext', {
                  amount: formatCurrency(overview.realized_pnl),
                  tradePct: formatPct(overview.realized_trade_return_pct),
                })}
                tone={overview.realized_pnl_pct >= 0 ? '#2563eb' : '#ef4444'}
              />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                pl: { sm: 1 },
                pt: { xs: 0.15, sm: 0 },
              }}
            >
              <Box sx={{ width: 44, height: 4, borderRadius: '999px', bgcolor: overview.unrealized_pnl_pct >= 0 ? alpha('#10b981', 0.9) : alpha('#ef4444', 0.9), mb: 1.05 }} />
              <SummaryStat
                label={t('paperTrading.modelBoard.unrealizedPnl')}
                value={formatPct(overview.unrealized_pnl_pct)}
                sub={formatCurrency(overview.unrealized_pnl)}
                tone={overview.unrealized_pnl_pct >= 0 ? '#10b981' : '#ef4444'}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.35fr 1fr' }, gap: 2 }}>
        <Paper elevation={0} sx={{ ...cardSx, p: 0 }} ref={allocationExportRef}>
          <Box sx={{ px: 2, py: 1.4, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b' }}>
                {t('paperTrading.modelBoard.currentAllocation')}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                {t('paperTrading.modelBoard.currentAllocationSubtitle')}
              </Typography>
            </Box>
            <Button
              data-html2canvas-ignore="true"
              variant="outlined"
              size="small"
              startIcon={<DownloadRounded sx={{ fontSize: 18 }} />}
              onClick={handleExportAllocationImage}
              disabled={exportingAllocationImage}
              sx={{
                minWidth: 0,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.75rem',
                borderRadius: 1,
                color: '#475569',
                borderColor: '#dbe4f0',
                bgcolor: '#fff',
                '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              }}
            >
              {exportingAllocationImage ? t('paperTrading.modelBoard.exportingImage') : t('paperTrading.modelBoard.exportImage')}
            </Button>
          </Box>
          <TableContainer sx={{ bgcolor: '#fff' }}>
            <Table size="small" sx={{ bgcolor: '#fff' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>{t('paperTrading.modelBoard.ticker')}</TableCell>
                  <TableCell align="center" sx={thSx}>{t('paperTrading.modelBoard.t25Status')}</TableCell>
                  <TableCell align="center" sx={thSx}>{t('paperTrading.modelBoard.holdingPeriod')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('paperTrading.modelBoard.avgEntry')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('paperTrading.modelBoard.currentPrice')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('paperTrading.modelBoard.positionPnl')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allocations.map((item) => {
                  const t25Meta = getT25Meta(item);
                  return (
                  <TableRow key={item.ticker} hover>
                    <TableCell sx={{ ...tdSx, fontWeight: 800, color: '#1d4ed8' }}>{item.ticker}</TableCell>
                    <TableCell align="center" sx={tdSx}>
                      <Chip label={t25Meta.label} size="small" sx={t25Meta.sx} />
                    </TableCell>
                    <TableCell align="center" sx={tdSx}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>
                        {formatHoldingPeriod(item)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace' }}>{formatCurrency(item.avg_entry_price)}</TableCell>
                    <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace' }}>{formatCurrency(item.current_price || 0)}</TableCell>
                    <TableCell align="right" sx={{ ...tdSx, color: item.unrealized_pnl >= 0 ? '#10b981' : '#ef4444' }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, fontFamily: 'monospace', color: 'inherit' }}>
                        {formatPct(item.unrealized_pnl_pct)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );})}
                {allocations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 5, textAlign: 'center', color: '#94a3b8' }}>
                      {t('paperTrading.modelBoard.noAllocations')}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper elevation={0} sx={{ ...cardSx, p: 0 }} ref={actionLogExportRef}>
          <Box sx={{ px: 2, py: 1.4, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b' }}>
                {t('paperTrading.modelBoard.actionLog')}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                {t('paperTrading.modelBoard.actionLogSubtitle')}
              </Typography>
            </Box>
            <Button
              data-html2canvas-ignore="true"
              variant="outlined"
              size="small"
              startIcon={<DownloadRounded sx={{ fontSize: 18 }} />}
              onClick={handleExportActionLogImage}
              disabled={exportingActionLogImage}
              sx={{
                minWidth: 0,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.75rem',
                borderRadius: 1,
                color: '#475569',
                borderColor: '#dbe4f0',
                bgcolor: '#fff',
                '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              }}
            >
              {exportingActionLogImage ? t('paperTrading.modelBoard.exportingImage') : t('paperTrading.modelBoard.exportImage')}
            </Button>
          </Box>
          <TableContainer sx={{ bgcolor: '#fff' }}>
            <Table size="small" sx={{ bgcolor: '#fff' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>{t('paperTrading.modelBoard.date')}</TableCell>
                  <TableCell sx={thSx}>{t('paperTrading.modelBoard.ticker')}</TableCell>
                  <TableCell sx={thSx}>{t('paperTrading.modelBoard.action')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('paperTrading.modelBoard.executionPrice')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('paperTrading.modelBoard.actionPnl')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {actionLog.map((item) => {
                  const isBuy = item.action === 'BUY';
                  const hasRealizedPnl = typeof item.realized_pnl_pct === 'number';
                  const pnlTone = (item.realized_pnl_pct || 0) >= 0 ? '#10b981' : '#ef4444';
                  return (
                    <TableRow key={item.trade_id} hover>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{new Date(item.fill_time).toLocaleDateString(locale)}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, fontWeight: 800 }}>{item.ticker}</TableCell>
                      <TableCell sx={tdSx}>
                        <Chip
                          label={isBuy ? t('paperTrading.modelBoard.actionLabels.buy') : t('paperTrading.modelBoard.actionLabels.sell')}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.66rem',
                            fontWeight: 700,
                            bgcolor: isBuy ? alpha('#10b981', 0.08) : alpha('#ef4444', 0.08),
                            color: isBuy ? '#10b981' : '#ef4444',
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 700 }}>
                        {formatCurrency(item.price || 0)}
                      </TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 700, color: hasRealizedPnl ? pnlTone : '#94a3b8' }}>
                        {hasRealizedPnl ? formatPct(item.realized_pnl_pct) : '...'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {actionLog.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 5, textAlign: 'center', color: '#94a3b8' }}>
                      {t('paperTrading.modelBoard.noActions')}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: '#1e293b' }}>
              {t('paperTrading.modelBoard.performanceOverTime')}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
              {t('paperTrading.modelBoard.performanceSubtitle')}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
            {timeline.length} {t('paperTrading.modelBoard.points')}
          </Typography>
        </Box>
        <Box sx={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <AreaChart data={timeline} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="portfolioPnlFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="shortDate" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(value) => `${value.toFixed(0)}%`} tickLine={false} axisLine={false} width={42} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="total_pnl_pct"
                stroke="#2563eb"
                strokeWidth={2.2}
                fill="url(#portfolioPnlFill)"
                name={t('paperTrading.modelBoard.portfolioGrowthPct')}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </Box>
  );
}
